import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    if (window.location.pathname.includes("cancelled")) {
      setStatus("cancelled");
      setMessage("Zahlung abgebrochen");
      return;
    }

    if (sessionId) {
      // Verify payment with backend
      verifyPayment(sessionId);
    } else {
      setStatus("error");
      setMessage("Keine Session-ID gefunden");
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      // Wait a bit for initial processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      // First, verify the session directly with Stripe and update user
      const verifyResponse = await axios.get(
        `${API}/stripe/verify-session/${sessionId}`,
        { withCredentials: true }
      );

      if (verifyResponse.data.status === "success") {
        setStatus("success");
        setMessage(`${verifyResponse.data.package_name || verifyResponse.data.tier?.toUpperCase()} Paket erfolgreich aktiviert!`);
        return;
      }

      // Fallback: Check user's package status
      const response = await axios.get(
        `${API}/stripe/subscription-status`,
        { withCredentials: true }
      );

      if (response.data.status === "active") {
        setStatus("success");
        setMessage(`${response.data.tier.toUpperCase()} Paket erfolgreich aktiviert!`);
      } else if (verifyResponse.data.payment_status === "paid") {
        // Payment was made but user update might have failed
        setStatus("success");
        setMessage("Zahlung erfolgreich! Dein Paket wird in Kürze aktiviert.");
      } else {
        setStatus("processing");
        setMessage("Zahlung wird verarbeitet...");
      }
    } catch (error) {
      console.error("Verification error:", error);
      // Don't show error immediately, might just be timing
      setStatus("processing");
      setMessage("Zahlung wird verarbeitet... Bitte warte einen Moment.");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-foreground mb-4">
              Zahlung wird verarbeitet...
            </h1>
            <p className="text-muted-foreground">
              Bitte warten Sie einen Moment
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-foreground mb-4">
              Zahlung erfolgreich!
            </h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="rounded-full px-8"
            >
              Zum Dashboard
            </Button>
          </>
        )}

        {status === "processing" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-foreground mb-4">
              Fast geschafft!
            </h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <Button
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="rounded-full px-8"
            >
              Weiter zum Dashboard
            </Button>
          </>
        )}

        {status === "cancelled" && (
          <>
            <XCircle className="w-16 h-16 text-yellow-600 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-foreground mb-4">
              Zahlung abgebrochen
            </h1>
            <p className="text-muted-foreground mb-8">
              Sie haben die Zahlung abgebrochen. Sie können jederzeit zurückkehren und ein Paket auswählen.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate("/select-package")}
                variant="outline"
                className="rounded-full px-8"
              >
                Paket wählen
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="rounded-full px-8"
              >
                Zum Dashboard
              </Button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-foreground mb-4">
              Fehler
            </h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate("/select-package")}
                variant="outline"
                className="rounded-full px-8"
              >
                Erneut versuchen
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="rounded-full px-8"
              >
                Zum Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
